import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE } from "utils";

function DaftarTamu() {
  const [dinasId, setDinasId] = useState(null);
  const [tamuList, setTamuList] = useState([]);
  const [filteredTamuList, setFilteredTamuList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState("");
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Generate tahun list (current year down to 2020)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2020; i--) {
    tahunList.push(i);
  }

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      fetchDaftarTamu(user.dinas_id, selectedTahun);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (dinasId) {
      fetchDaftarTamu(dinasId, selectedTahun);
    }
  }, [selectedTahun, dinasId]);

  const fetchDaftarTamu = async (idDinas, tahun) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/daftar-tamu/${idDinas}?tahun=${tahun}&limit=100&offset=0`
      );
      const data = response.data.data || [];
      setTamuList(data);
      setFilteredTamuList(data);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching daftar tamu:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data daftar tamu",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter data berdasarkan search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTamuList(tamuList);
    } else {
      const filtered = tamuList.filter((tamu) => {
        const searchLower = searchTerm.toLowerCase();
        const nama = (tamu.nama || "").toLowerCase();
        const namaInstansi = (tamu.nama_instansi || "").toLowerCase();
        const keperluan = (tamu.keperluan || "").toLowerCase();
        return (
          nama.includes(searchLower) ||
          namaInstansi.includes(searchLower) ||
          keperluan.includes(searchLower)
        );
      });
      setFilteredTamuList(filtered);
    }
  }, [searchTerm, tamuList]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    { Header: "Nama", accessor: "nama", width: "15%" },
    {
      Header: "Nama Instansi",
      accessor: "nama_instansi",
      width: "200px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Keperluan", accessor: "keperluan", width: "25%" },
    {
      Header: "Bidang",
      accessor: "nama_bidang",
      width: "150px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "150px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Bertemu Dengan", accessor: "nama_bertemu_dengan", width: "13%" },
    { Header: "Tanggal", accessor: "tanggal", width: "10%" },
  ];

  const rows = filteredTamuList.map((tamu, index) => ({
    id: tamu.id,
    no: index + 1,
    nama: tamu.nama || "-",
    nama_instansi: tamu.nama_instansi || "-",
    keperluan: tamu.keperluan || "-",
    nama_bidang: tamu.nama_bidang || "-",
    nama_bertemu_dengan: tamu.nama_bertemu_dengan || "-",
    tanggal: formatDate(tamu.tanggal),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white" mb={2}>
                  Daftar Tamu
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                >
                  <MDInput
                    label="Cari (Nama, Instansi, atau Keperluan)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      flexGrow: 1,
                      minWidth: 200,
                    }}
                  />
                  <Autocomplete
                    options={tahunList}
                    value={selectedTahun}
                    onChange={(event, newValue) => {
                      if (newValue) setSelectedTahun(newValue);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Pilih Tahun"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                    getOptionLabel={(option) => option.toString()}
                  />
                </MDBox>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : isMobile ? (
                  <MDBox px={3}>
                    {filteredTamuList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data tamu
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchTerm
                            ? "Tidak ada data yang sesuai dengan pencarian"
                            : `Belum ada tamu yang terdaftar untuk tahun ${selectedTahun}`}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      filteredTamuList.map((tamu, index) => (
                        <Card key={tamu.id} sx={{ mb: 2 }}>
                          <MDBox p={2}>
                            <MDTypography variant="h6" fontWeight="bold">
                              {tamu.nama_instansi || "-"}
                            </MDTypography>
                            {tamu.nama && (
                              <MDBox mt={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Nama:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {tamu.nama}
                                </MDTypography>
                              </MDBox>
                            )}
                            <MDBox mt={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Keperluan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.keperluan || "-"}
                              </MDTypography>
                            </MDBox>
                            <MDBox mt={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Bidang:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_bidang || "-"}
                              </MDTypography>
                            </MDBox>
                            <MDBox mt={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Bertemu Dengan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_bertemu_dengan || "-"}
                              </MDTypography>
                            </MDBox>
                            <MDBox mt={1}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Tanggal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {formatDate(tamu.tanggal)}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </Card>
                      ))
                    )}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns,
                      rows,
                    }}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    canSearch={false}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DaftarTamu;
